"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Bot, Phone, PhoneOff, Sparkles, Mic, Languages, CalendarClock, ShieldCheck } from "lucide-react";

interface Turn {
  from: "ai" | "client";
  text: string;
}

const SCRIPT: Turn[] = [
  { from: "ai", text: "Namaste! This is Ava, the AI voice assistant from Johnson Securities. Am I speaking with Mr. Rajesh Kumar?" },
  { from: "client", text: "Yes, that's me." },
  { from: "ai", text: "Great! I'm calling because your MCX commodity segment activation is pending an income proof. Shall I send you a secure WhatsApp link to upload your latest bank statement?" },
  { from: "client", text: "Yes please, send it now." },
  { from: "ai", text: "Done ✅ — the upload link is on its way to your WhatsApp. Once verified, MCX will be active within 24 hours. Would you also like me to book a callback with your Relationship Manager, Aditya?" },
  { from: "client", text: "Sure, tomorrow morning works." },
  { from: "ai", text: "Perfect. I've scheduled a callback for 10:30 AM tomorrow and logged everything to your CRM timeline. Have a great trading day!" },
];

const CAPABILITIES = [
  { icon: Languages, title: "10+ Indian languages", desc: "Hindi, Tamil, Telugu, Kannada, Bengali & more with natural code-switching." },
  { icon: CalendarClock, title: "Books & logs actions", desc: "Schedules callbacks, sends links and writes to the lead's activity history." },
  { icon: ShieldCheck, title: "Compliant by design", desc: "Discloses it is an AI, records consent, and never gives investment advice." },
];

export function AiVoiceAssistantTab() {
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(false);
  const [visible, setVisible] = useState<Turn[]>([]);
  const [seconds, setSeconds] = useState(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);

  function clearAll() {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (tick.current) clearInterval(tick.current);
    tick.current = null;
  }

  function startCall() {
    clearAll();
    setActive(true);
    setVisible([]);
    setSeconds(0);
    tick.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    SCRIPT.forEach((turn, i) => {
      timers.current.push(
        setTimeout(() => {
          setVisible((prev) => [...prev, turn]);
          if (i === SCRIPT.length - 1) {
            timers.current.push(setTimeout(() => endCall(), 2600));
          }
        }, i * 1900 + 600)
      );
    });
  }

  function endCall() {
    clearAll();
    setActive(false);
  }

  useEffect(() => () => clearAll(), []);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Bot className="w-5 h-5 text-emerald-600" /> AI Voice Assistant
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">Concept</span>
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">Autonomous outbound voice agent for KYC nudges, activation follow-ups and callbacks.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Call panel */}
        <div className="lg:col-span-2 p-6 rounded-2xl bg-gradient-to-b from-white to-slate-50 border border-slate-200">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                  <Bot className="w-6 h-6 text-slate-900" />
                </div>
                {active && <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-white animate-pulse" />}
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900">Ava · AI Voice Agent</div>
                <div className="text-[11px] text-slate-500">{active ? `On call · ${mm}:${ss}` : "Ready to dial"}</div>
              </div>
            </div>

            {/* Waveform */}
            <div className="flex items-end gap-0.5 h-8">
              {[...Array(16)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-1 rounded-full bg-emerald-400/80"
                  animate={active && !reduceMotion ? { height: [4, 8 + (i % 5) * 5, 4] } : { height: 4 }}
                  transition={{ duration: 0.6 + (i % 4) * 0.15, repeat: active && !reduceMotion ? Infinity : 0, ease: "easeInOut" }}
                />
              ))}
            </div>
          </div>

          {/* Transcript */}
          <div className="min-h-[240px] max-h-[300px] overflow-y-auto rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2.5">
            {visible.length === 0 && !active && (
              <div className="h-full flex flex-col items-center justify-center text-center py-10 text-slate-500">
                <Mic className="w-8 h-8 mb-2 text-slate-600" />
                <p className="text-xs">Press <strong className="text-emerald-600">Start AI Voice Call</strong> to watch a live activation follow-up.</p>
              </div>
            )}
            {visible.map((turn, i) => (
              <motion.div
                key={i}
                initial={reduceMotion ? {} : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${turn.from === "ai" ? "justify-start" : "justify-end"}`}
              >
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${turn.from === "ai" ? "bg-emerald-100 text-emerald-900 border border-emerald-500/25 rounded-bl-sm" : "bg-slate-100 text-slate-800 rounded-br-sm"}`}>
                  <span className="block text-[9px] font-bold uppercase tracking-wider opacity-60 mb-0.5">{turn.from === "ai" ? "Ava (AI)" : "Client"}</span>
                  {turn.text}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mt-5">
            {!active ? (
              <button onClick={startCall} className="py-2.5 px-6 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-all">
                <Phone className="w-4 h-4" /> Start AI Voice Call
              </button>
            ) : (
              <button onClick={endCall} className="py-2.5 px-6 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs flex items-center gap-2 transition-all">
                <PhoneOff className="w-4 h-4" /> End Call
              </button>
            )}
          </div>
        </div>

        {/* Capabilities */}
        <div className="space-y-4">
          <div className="p-5 rounded-2xl bg-white border border-slate-200">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-emerald-600" /> What Ava can do</h3>
            <div className="space-y-3">
              {CAPABILITIES.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-900">{c.title}</div>
                      <div className="text-[10px] text-slate-500 leading-relaxed">{c.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/20 text-[10px] text-amber-700 leading-relaxed">
            Concept demo. Production integrates a telephony + TTS/ASR voice stack (e.g. Exotel / Twilio + speech models) with human hand-off.
          </div>
        </div>
      </div>
    </div>
  );
}
